import React from 'react';

/** Room a caption keeps clear of its band's two edges, together. */
export const CAPTION_INSET_PX = 8;

/** Whether each band's caption fits on its fill, keyed by the band's index in `segments`. */
export type CaptionFits = Record<number, boolean>;

type NodeRefs = React.MutableRefObject<Record<number, HTMLElement | null>>;

const sameFits = (a: CaptionFits | null, b: CaptionFits | null): boolean => {
  if (a === null || b === null) {
    return a === b;
  }

  const keys = Object.keys(a);

  return keys.length === Object.keys(b).length && keys.every(key => a[+key] === b[+key]);
};

/* A caption's lines are flex items of a column that never stretches them, and they never wrap, so
   each line's box is the width of its text whether the caption is painted or visually hidden. */
const readFits = (bands: NodeRefs['current'], captions: NodeRefs['current']): CaptionFits =>
  Object.keys(bands).reduce((fits: CaptionFits, key) => {
    const index = +key;
    const band = bands[index];
    const caption = captions[index];

    if (!band || !caption) {
      return fits;
    }

    const captionWidth = Math.max(
      0,
      ...Array.from(caption.children).map(line => line.getBoundingClientRect().width)
    );

    return {
      ...fits,
      [index]: captionWidth + CAPTION_INSET_PX <= band.getBoundingClientRect().width
    };
  }, {});

/**
 * Which band captions fit their band on the laid-out bar, re-read when the bar resizes and once its
 * fonts have loaded. `null` until the bar has a width — before layout, and always under jsdom — so
 * the caller keeps a fallback for that window.
 */
export const useCaptionFits = (
  barRef: React.RefObject<HTMLElement>,
  bandRefs: NodeRefs,
  captionRefs: NodeRefs,
  signature: string
): CaptionFits | null => {
  const [fits, setFits] = React.useState<CaptionFits | null>(null);

  React.useLayoutEffect(() => {
    const bar = barRef.current;

    if (!bar) {
      return undefined;
    }

    let live = true;
    const measure = () => {
      if (!live) {
        return;
      }

      const next = bar.clientWidth > 0 ? readFits(bandRefs.current, captionRefs.current) : null;

      setFits(previous => (sameFits(previous, next) ? previous : next));
    };

    measure();

    const observer =
      typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(() => measure());

    observer?.observe(bar);
    document.fonts?.ready.then(measure).catch(() => undefined);

    return () => {
      live = false;
      observer?.disconnect();
    };
  }, [barRef, bandRefs, captionRefs, signature]);

  return fits;
};
